import {
  useParams,
  type AnyRoute,
  type LinkProps,
} from "@tanstack/react-router";
import { useMemo } from "react";

import { useIsTextMessagingActive } from "@/api/siteCapabilities";
import { isBlastroEnabledForSite } from "@/features/Blastro/installationLink";
import { isSavedBlockTypesEnabledForSite } from "@/features/BlockLibrary/flag";
import { useIsDocsEnabled } from "@/features/Docs/flag";
import { useIsGenerationsEnabled } from "@/features/Generations/flag";
import { useIsGenLabEnabled } from "@/features/GenLab/flag";
import { useIsMarketingCalendarEnabledForSite } from "@/features/MarketingCalendar/flag";
import { useIsBillingPageEnabled } from "@/features/Settings/flag";
import { useMachineAppsAccess } from "@/features/Settings/machineAppsAccess";
import { useSmsRegistrationAccess } from "@/features/Settings/smsRegistrationAccess";
import { Route as assetsRoute } from "@/routes/_auth/assets.$siteId/route";
import { Route as bestiesRoute } from "@/routes/_auth/besties.$siteId/route";
import { Route as blocksRoute } from "@/routes/_auth/blocks.$siteId/route";
import { Route as setupRoute } from "@/routes/_auth/brand-onboarding.$siteId.$stepId";
import { Route as creativeSphereRoute } from "@/routes/_auth/creative-sphere.$siteId/route";
import { Route as dimensionsRoute } from "@/routes/_auth/dimensions.$siteId/route";
import { Route as docsRoute } from "@/routes/_auth/docs.$siteId";
import { Route as dragonTilesConnectorsRoute } from "@/routes/_auth/dragon-tiles.$siteId/connectors/route";
import { Route as dragonTilesRoute } from "@/routes/_auth/dragon-tiles.$siteId/route";
import { Route as exchangesRoute } from "@/routes/_auth/exchanges.$siteId/route";
import { Route as generationsRoute } from "@/routes/_auth/generations.$siteId/route";
import { Route as genlabRoute } from "@/routes/_auth/genlab.$siteId/route";
import { Route as marketingCalendarRoute } from "@/routes/_auth/marketing-calendar.$siteId/route";
import { Route as audienceRoute } from "@/routes/_auth/people.$siteId/audiences/route";
import { Route as listsRoute } from "@/routes/_auth/people.$siteId/lists/route";
import { Route as profilesRoute } from "@/routes/_auth/people.$siteId/profiles/route";
import { Route as zeroAccountsRoute } from "@/routes/_auth/settings.$siteId/_zeroAdmin/accounts/index";
import { Route as zeroBlueprintsRoute } from "@/routes/_auth/settings.$siteId/_zeroAdmin/blueprints/index";
import { Route as zeroCopyFramesRoute } from "@/routes/_auth/settings.$siteId/_zeroAdmin/copy-frames";
import { Route as zeroNotificationsRoute } from "@/routes/_auth/settings.$siteId/_zeroAdmin/notifications/index";
import { Route as zeroOnboardingRoute } from "@/routes/_auth/settings.$siteId/_zeroAdmin/onboarding/index";
import { Route as zeroSitesRoute } from "@/routes/_auth/settings.$siteId/_zeroAdmin/sites/index";
import { Route as billingRoute } from "@/routes/_auth/settings.$siteId/billing";
import { Route as connectorsRoute } from "@/routes/_auth/settings.$siteId/connectors/route";
import { Route as couponCodesRoute } from "@/routes/_auth/settings.$siteId/coupon-codes/route";
import { Route as emailSettingsRoute } from "@/routes/_auth/settings.$siteId/email";
import { Route as featureFlagsRoute } from "@/routes/_auth/settings.$siteId/feature-flags";
import { Route as generalSettingsRoute } from "@/routes/_auth/settings.$siteId/general";
import { Route as machineApplicationsRoute } from "@/routes/_auth/settings.$siteId/machine-applications";
import { Route as mockConfigRoute } from "@/routes/_auth/settings.$siteId/mock-config";
import { Route as seedBestiesSitesRoute } from "@/routes/_auth/settings.$siteId/seed-besties-sites";
import { Route as smsRegistrationRoute } from "@/routes/_auth/settings.$siteId/sms-registration";
import { Route as textSettingsRoute } from "@/routes/_auth/settings.$siteId/text";
import { Route as skuStoreRoute } from "@/routes/_auth/sku-store.$siteId/route";
import { Route as blastroConnectRoute } from "@/routes/blastro.$siteId.connect";
import { useIsDemoUser } from "@/utils/demoMode";
import { isDynamicFeatureFlagsEnabled } from "@/utils/featureFlagOverride";
import { isMockConfigAvailable } from "@/utils/mockConfig";
import {
  isPrivilegedDevToolsVisible,
  useHasElevatedAccess,
  useIsRealRoleElevated,
} from "@/utils/privilegedMode";

export interface SubLink {
  label: string;
  route: AnyRoute;
  params?: LinkProps["params"];
  disabled?: boolean;
}

export interface NavGroup {
  label: string;
  defaultRoute: AnyRoute;
  subLinks?: SubLink[];
}

const isSkuStoreEnabled = import.meta.env.VITE_FF_ENABLE_SKU_STORE === "true";

const isAssetLibraryEnabled =
  import.meta.env.VITE_FF_ENABLE_ASSET_LIBRARY === "true";

// Nav allowlist for read-only demo accounts (FOG-2234). Deliberately an
// allowlist, not a blocklist: features that launch later (flag flips, new
// sections) stay hidden from demo prospects until consciously added here.
const DEMO_ALLOWED_NAV: Record<string, Set<string>> = {
  Connect: new Set(["Dragon Tiles", "Connectors", "Exchanges"]),
  Creativesphere: new Set(["Tiles", "Brand Book"]),
};

function redactGroupsForDemo(groups: NavGroup[]): NavGroup[] {
  return groups
    .filter((group) => group.label in DEMO_ALLOWED_NAV)
    .map((group) => ({
      ...group,
      subLinks: group.subLinks?.filter((link) =>
        DEMO_ALLOWED_NAV[group.label].has(link.label),
      ),
    }));
}

function getNavGroups(
  hasElevatedAccess: boolean,
  isRealRoleElevated: boolean,
  smsEnabled: boolean,
  billingEnabled: boolean,
  genlabEnabled: boolean,
  marketingCalendarEnabled: boolean,
  generationsEnabled: boolean,
  machineAppsAccess: boolean,
  blocksEnabled: boolean,
  docsEnabled: boolean,
  smsRegistrationAccess: boolean,
  siteId?: string,
): NavGroup[] {
  const showPrivilegedDevTools =
    isPrivilegedDevToolsVisible(isRealRoleElevated);
  const showFeatureFlags =
    showPrivilegedDevTools ||
    (isDynamicFeatureFlagsEnabled && hasElevatedAccess);

  return [
    {
      label: "Connect",
      defaultRoute: dragonTilesRoute,
      subLinks: [
        { label: "Dragon Tiles", route: dragonTilesRoute },
        { label: "Connectors", route: dragonTilesConnectorsRoute },
        { label: "Exchanges", route: exchangesRoute },
        { label: "Coupon Codes", route: couponCodesRoute },
        ...(marketingCalendarEnabled && hasElevatedAccess
          ? [{ label: "Marketing Calendar", route: marketingCalendarRoute }]
          : []),
      ],
    },
    {
      label: "Creativesphere",
      defaultRoute: creativeSphereRoute,
      subLinks: [
        { label: "Tiles", route: creativeSphereRoute },
        ...(blocksEnabled ? [{ label: "Blocks", route: blocksRoute }] : []),
        ...(generationsEnabled
          ? [{ label: "Propagation", route: generationsRoute }]
          : []),
        ...(isAssetLibraryEnabled
          ? [{ label: "Asset Library", route: assetsRoute }]
          : []),
        {
          label: "Brand Book",
          route: setupRoute,
          params: { stepId: "styling" },
        },
        ...(genlabEnabled ? [{ label: "GenLab", route: genlabRoute }] : []),
      ],
    },
    ...(isSkuStoreEnabled || hasElevatedAccess
      ? [
          {
            label: "SKU Store",
            defaultRoute: skuStoreRoute,
          },
        ]
      : []),
    {
      label: "People",
      defaultRoute: audienceRoute,
      subLinks: [
        { label: "Audiences", route: audienceRoute },
        { label: "Lists", route: listsRoute },
        { label: "Profiles", route: profilesRoute },
      ],
    },
    {
      label: "Reporting",
      defaultRoute: dimensionsRoute,
    },
    {
      label: "Besties",
      defaultRoute: bestiesRoute,
    },
    {
      label: "Settings",
      defaultRoute: generalSettingsRoute,
      subLinks: [
        { label: "General", route: generalSettingsRoute },
        { label: "Email", route: emailSettingsRoute },
        ...(smsEnabled ? [{ label: "Text", route: textSettingsRoute }] : []),
        ...(smsRegistrationAccess
          ? [{ label: "SMS registration", route: smsRegistrationRoute }]
          : []),
        ...(billingEnabled ? [{ label: "Billing", route: billingRoute }] : []),
        { label: "Connectors", route: connectorsRoute },
        ...(machineAppsAccess
          ? [{ label: "Machine Applications", route: machineApplicationsRoute }]
          : []),
        ...(isBlastroEnabledForSite(siteId)
          ? [{ label: "Blastro", route: blastroConnectRoute }]
          : []),
        ...(hasElevatedAccess
          ? [{ label: "Seed Besties Sites", route: seedBestiesSitesRoute }]
          : []),
        ...(isMockConfigAvailable
          ? [{ label: "Mock Data Config", route: mockConfigRoute }]
          : []),
        ...(showFeatureFlags
          ? [{ label: "Feature Flags", route: featureFlagsRoute }]
          : []),
        ...(hasElevatedAccess
          ? [
              { label: "Sites", route: zeroSitesRoute },
              { label: "Accounts", route: zeroAccountsRoute },
              { label: "Onboarding", route: zeroOnboardingRoute },
              { label: "Copy Tiles", route: zeroCopyFramesRoute },
              { label: "Blueprint Review", route: zeroBlueprintsRoute },
              { label: "Notifications", route: zeroNotificationsRoute },
            ]
          : []),
      ],
    },
    ...(docsEnabled ? [{ label: "Docs", defaultRoute: docsRoute }] : []),
  ];
}

export function useNavGroups(): NavGroup[] {
  const isDemoUser = useIsDemoUser();
  const hasElevatedAccess = useHasElevatedAccess();
  const isRealRoleElevated = useIsRealRoleElevated();
  const params = useParams({ strict: false });
  const siteId = params.siteId;
  const smsEnabled = useIsTextMessagingActive(params.siteId ?? "");
  const billingEnabled = useIsBillingPageEnabled();
  const genlabEnabled = useIsGenLabEnabled();
  const marketingCalendarEnabled = useIsMarketingCalendarEnabledForSite(
    params.siteId,
  );
  const generationsEnabled = useIsGenerationsEnabled();
  const machineAppsAccess = useMachineAppsAccess();
  const blocksEnabled = isSavedBlockTypesEnabledForSite(params.siteId);
  const docsEnabled = useIsDocsEnabled();
  const smsRegistrationAccess = useSmsRegistrationAccess();

  return useMemo(() => {
    const groups = getNavGroups(
      hasElevatedAccess,
      isRealRoleElevated,
      smsEnabled,
      billingEnabled,
      genlabEnabled,
      marketingCalendarEnabled,
      generationsEnabled,
      machineAppsAccess,
      blocksEnabled,
      docsEnabled,
      smsRegistrationAccess,
      siteId,
    );
    return isDemoUser ? redactGroupsForDemo(groups) : groups;
  }, [
    isDemoUser,
    hasElevatedAccess,
    isRealRoleElevated,
    smsEnabled,
    billingEnabled,
    genlabEnabled,
    marketingCalendarEnabled,
    generationsEnabled,
    machineAppsAccess,
    blocksEnabled,
    docsEnabled,
    smsRegistrationAccess,
    siteId,
  ]);
}
